import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { StepProps } from '../MultiStepModal';
import { useCertificateTemplatesData } from '../../../hooks/useData';
import { isPdfChecklistTemplate } from '../../../constants/requestCertificateTemplates';
import { JOB_CERTIFICATE_KIND_CP17_BREAKDOWN } from '../../../constants/lockedCertificates';

function countWizardFields(schema: any): number {
  const screens: any[] = Array.isArray(schema?.screens) ? schema.screens : [];
  let n = 0;
  for (const s of screens) {
    const sections: any[] = Array.isArray(s?.sections) ? s.sections : [];
    for (const sec of sections) {
      const fields: any[] = Array.isArray(sec?.fields) ? sec.fields : [];
      n += fields.length;
    }
  }
  return n;
}

/** Engineer requests: locked product type (bundled CP17 PDF + fixed wizard). Office fill: DB templates. */
export default function CertificateTemplateSelectStep({ data, onDataChange }: StepProps) {
  const { data: templates, isLoading, isError } = useCertificateTemplatesData();
  const forRequest = String((data as any)?.certificateFlow || '') === 'request';
  const certificateKind = String((data as any)?.certificateKind || '');

  useEffect(() => {
    if (!forRequest) return;
    if (certificateKind === JOB_CERTIFICATE_KIND_CP17_BREAKDOWN) return;
    onDataChange({
      certificateKind: JOB_CERTIFICATE_KIND_CP17_BREAKDOWN,
      certificateTypeLabel: 'CP17 — Breakdown',
      templateId: '',
      selectedTemplate: null,
      filledData: {},
    });
  }, [forRequest, certificateKind, onDataChange]);

  const options = useMemo(() => {
    const arr: any[] = Array.isArray(templates) ? templates : [];
    const filtered = arr.filter((t) => isPdfChecklistTemplate(t));

    return filtered.map((t) => ({
      id: String(t?.id || ''),
      label: String(t?.templateLabel || t?.certificateTypeName || t?.certificatePrefix || 'Template'),
      typeName: String(t?.certificateTypeName || ''),
      fieldCount: (() => {
        const fromSchema = countWizardFields(t?.schemaJson);
        if (fromSchema) return fromSchema;
        return Array.isArray(t?.fields) ? t.fields.length : 0;
      })(),
      pathHint: String(t?.pdfStoragePath || ''),
      template: t,
    }));
  }, [templates]);

  const selectedId = String(data.templateId || '');

  if (forRequest) {
    return (
      <div className="step-form">
        <div className="step-header">
          <h3 className="step-form-title">Certificate type</h3>
          <p className="step-form-subtitle">
            Requests use the locked CP17 Breakdown checklist: company-standard PDF, fixed steps, and AcroForm field keys.
            Additional certificate types will be added here later.
          </p>
        </div>

        <div className="step-form-content">
          <div className="rounded-xl border border-[#2563eb] bg-[#eff6ff] p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-[#181818]">CP17 — Breakdown</div>
                <div className="mt-1 text-xs leading-relaxed text-[#686E6C]">
                  Non-domestic gas safety record. Engineers complete job → site → appliances → pipework → safety → sign-off on
                  site; the server fills the master PDF.
                </div>
              </div>
              <span className="shrink-0 rounded-full bg-[#2563eb] px-2 py-0.5 text-[10px] font-semibold uppercase text-white">
                Locked
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="step-form">
      <div className="step-header">
        <h3 className="step-form-title">Certificate type</h3>
        <p className="step-form-subtitle">Choose which certificate PDF template to use.</p>
      </div>

      <div className="step-form-content">
        {isLoading ? <div className="text-sm text-[#686E6C]">Loading templates…</div> : null}
        {isError ? <div className="text-sm text-red-700">Failed to load templates.</div> : null}

        {!isLoading && !isError && options.length === 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
            <p className="font-semibold">No PDF checklist templates yet</p>
            <p className="mt-2 text-xs leading-relaxed opacity-90">
              Upload a PDF in Settings, extract fields, then return here to fill a certificate.
            </p>
            <Link
              to="/dashboard/settings/certificate-templates"
              className="mt-3 inline-block text-xs font-semibold text-amber-900 underline"
            >
              Open certificate templates
            </Link>
          </div>
        ) : null}

        {!isLoading && options.length > 0 ? (
          <div className="space-y-2">
            <div className="text-xs font-medium uppercase tracking-wide text-[#686E6C]">Select template *</div>
            <div className="grid grid-cols-1 gap-2 max-h-[280px] overflow-y-auto pr-1">
              {options.map((o) => {
                const selected = selectedId === o.id;
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() =>
                      onDataChange({
                        templateId: o.id,
                        selectedTemplate: o.template,
                        filledData: {},
                      })
                    }
                    className={`rounded-xl border p-3 text-left transition-colors ${
                      selected
                        ? 'border-[#2563eb] bg-[#eff6ff]'
                        : 'border-[#E6E6E4] bg-white hover:bg-[#FAFAF9]'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-[#181818]">{o.label}</div>
                        {o.typeName ? <div className="mt-0.5 text-xs text-[#686E6C]">{o.typeName}</div> : null}
                        <div className="mt-1 text-[11px] text-[#686E6C]">{o.fieldCount} fields</div>
                      </div>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                          selected ? 'bg-[#2563eb] text-white' : 'bg-[#F3F4F6] text-[#4B5563]'
                        }`}
                      >
                        PDF
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
